/*
 * WP-10 session context (§14.7): loads GET /auth/session, exposes the
 * customer/session, redirects to /login?next= on PORTAL_SESSION_INVALID, and
 * warns shortly before the absolute session expiry.
 *
 * The CSRF token is session-bound server-side: after login, activation or
 * logout the cached token must be dropped and re-fetched (clearCsrfToken).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearCsrfToken, isPortalApiError, portalApi } from '../api/portal-api';
import type { SessionResponse } from '../api/types';
import { clearPortalOwnerState, discardLegacyDraft, setPortalOwnerScope } from '../utils/selection';
import {
  SessionContext,
  useSession,
  type SessionContextValue,
  type SessionState,
} from './session-context';

const EXPIRY_WARNING_SECONDS = 300;

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const [expirySecondsLeft, setExpirySecondsLeft] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const redirected = useRef(false);
  // FIN-01: `sessionSeq` orders session loads — only the most recently issued
  // request may write state, so an overlapping refresh or one issued before
  // sign-out can never resurrect an older (or any) authenticated view.
  const sessionSeq = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // `load` keeps a stable identity (location via ref); navigation-triggered
  // refetches are handled by the dedicated effect below.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const load = useCallback(async () => {
    const seq = ++sessionSeq.current;
    try {
      const session = await portalApi.get<SessionResponse>('/auth/session');
      if (!mountedRef.current || seq !== sessionSeq.current) return;
      // Owner-scoped draft/intent storage (F1): set before authed pages render.
      setPortalOwnerScope(session.customer.ownerScope ?? null);
      discardLegacyDraft();
      setState({ status: 'authed', session });
      redirected.current = false;
      // Session-bound CSRF token supersedes any pre-session token — force
      // re-fetch so the next mutation sends a valid token.
      clearCsrfToken();
    } catch (err) {
      if (!mountedRef.current || seq !== sessionSeq.current) return;
      setPortalOwnerScope(null);
      setState({ status: 'anonymous' });
      const loc = locationRef.current;
      if (
        isPortalApiError(err, 'PORTAL_SESSION_INVALID') &&
        !redirected.current &&
        !loc.pathname.startsWith('/login') &&
        !loc.pathname.startsWith('/activate') &&
        !loc.pathname.startsWith('/legal')
      ) {
        redirected.current = true;
        const returnTo = loc.pathname + loc.search + loc.hash;
        navigate(`/login?next=${encodeURIComponent(returnTo)}`, { replace: true });
      }
    }
  }, [navigate]);

  // FIN-01: session loads fire on mount and on EVERY SPA navigation — the
  // refetch is how a server-side owner flip is detected promptly (F1.3c) —
  // EXCEPT the navigation that lands on /login after signOut/signOutAll: a
  // fresh probe there could resurrect the just-revoked session. The
  // suppression clears on the next non-/login navigation.
  const suppressNavRef = useRef(false);
  const lastNavKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastNavKeyRef.current === location.key) return;
    lastNavKeyRef.current = location.key;
    if (suppressNavRef.current && location.pathname.startsWith('/login')) return;
    suppressNavRef.current = false;
    void load();
  }, [location.key, location.pathname, load]);

  // Absolute-expiry countdown (the idle window is enforced server-side).
  useEffect(() => {
    if (state.status !== 'authed' || !state.session.absoluteExpiresAt) {
      setExpirySecondsLeft(null);
      return;
    }
    const tick = () => {
      const ms = new Date(state.session.absoluteExpiresAt!).getTime() - Date.now();
      setExpirySecondsLeft(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const timer = window.setInterval(tick, 15_000);
    return () => window.clearInterval(timer);
  }, [state]);

  const signOut = useCallback(async () => {
    // Invalidate in-flight session loads first — a pre-logout refresh must
    // never restore authenticated state after the member signs out (FIN-01).
    sessionSeq.current += 1;
    let revoked = false;
    try {
      await portalApi.post('/auth/logout');
      revoked = true;
    } finally {
      // Only confirmed revocation clears this owner's draft/intent — a failed
      // logout POST must not discard a recoverable pending submission.
      if (revoked) clearPortalOwnerState();
      clearCsrfToken();
      setPortalOwnerScope(null);
      setState({ status: 'anonymous' });
      suppressNavRef.current = true;
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const signOutAll = useCallback(async () => {
    sessionSeq.current += 1;
    // No finally here: a cold step-up grant makes the POST throw
    // PORTAL_STEP_UP_REQUIRED, and clearing state would unmount the
    // StepUpProvider before the user can complete the ceremony — the retry
    // (and thus the real server-side revocation) would never run.
    await portalApi.post('/auth/logout-all');
    clearPortalOwnerState();
    clearCsrfToken();
    setPortalOwnerScope(null);
    setState({ status: 'anonymous' });
    suppressNavRef.current = true;
    navigate('/login', { replace: true });
  }, [navigate]);

  const value = useMemo<SessionContextValue>(() => {
    const stepUpValid =
      state.status === 'authed' &&
      !!state.session.stepUpValidUntil &&
      Date.parse(state.session.stepUpValidUntil) > Date.now();
    return {
      state,
      customer: state.status === 'authed' ? state.session.customer : null,
      stepUpValid,
      refresh: load,
      signOut,
      signOutAll,
      expirySecondsLeft,
    };
  }, [state, load, signOut, signOutAll, expirySecondsLeft]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Renders the expiry warning toast (mounted by the app shell). */
export function SessionExpiryWarning() {
  const { t } = useTranslation();
  const { state, expirySecondsLeft } = useSession();
  if (
    state.status !== 'authed' ||
    expirySecondsLeft === null ||
    expirySecondsLeft > EXPIRY_WARNING_SECONDS
  ) {
    return null;
  }
  const minutes = Math.max(1, Math.ceil(expirySecondsLeft / 60));
  return (
    <div className="hp-toast" role="status" data-quality-id="session.expiryWarning">
      {t('common.sessionEnding', { minutes })}
    </div>
  );
}
